import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose,
} from "./ui/dialog";
import { CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Reusable Mark-As-Sold dialog used by both MyListings and ListingDetail.
 * Supports two sale channels: "adukoo" (chat buyer) and "external" (sold outside Adukoo).
 * Props:
 *   listing: {id, title, quantity, sold_quantity}
 *   onSold: () => void — invoked after a successful sale
 *   trigger: optional custom trigger node (defaults to yellow "Mark as Sold" pill)
 */
export default function MarkSoldDialog({ listing, onSold, trigger }) {
    const available = Math.max(0, (listing.quantity || 1) - (listing.sold_quantity || 0));
    const [qty, setQty] = useState("1");
    const [buyer, setBuyer] = useState("");
    const [channel, setChannel] = useState("adukoo");
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);

    const submit = async () => {
        const q = parseInt(qty, 10);
        if (!q || q < 1) return toast.error("Enter a valid quantity");
        if (q > available) return toast.error(`Only ${available} unit(s) available`);
        setBusy(true);
        try {
            await api.post(`/listings/${listing.id}/mark-sold`, {
                quantity_sold: q,
                buyer_name: buyer.trim() || null,
                sold_externally: channel === "external",
            });
            toast.success(channel === "external" ? `Marked ${q} unit(s) sold outside Adukoo` : `Marked ${q} unit(s) as sold`);
            setOpen(false);
            setQty("1"); setBuyer(""); setChannel("adukoo");
            onSold?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {trigger || (
                    <Button data-testid={`mark-sold-${listing.id}`} disabled={available <= 0} className="aduku-yellow-btn hover:bg-aduku-yellow-600 rounded-full disabled:opacity-50">
                        <CheckCircle2 className="w-4 h-4 mr-1.5" /> {available <= 0 ? "Sold out" : "Mark as Sold"}
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Mark as sold</DialogTitle>
                    <DialogDescription>Record a sale for "{listing.title}". {available} unit(s) currently in stock.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Sold through</label>
                        <div className="mt-1.5 grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                data-testid="mark-sold-channel-adukoo"
                                onClick={() => setChannel("adukoo")}
                                className={`h-10 rounded-full text-sm font-semibold border transition ${channel === "adukoo" ? "bg-aduku-navy text-white border-aduku-navy" : "bg-white text-aduku-navy border-aduku-border hover:border-aduku-navy"}`}
                            >On Adukoo</button>
                            <button
                                type="button"
                                data-testid="mark-sold-channel-external"
                                onClick={() => setChannel("external")}
                                className={`h-10 rounded-full text-sm font-semibold border transition ${channel === "external" ? "bg-aduku-navy text-white border-aduku-navy" : "bg-white text-aduku-navy border-aduku-border hover:border-aduku-navy"}`}
                            >Outside Adukoo</button>
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Quantity sold *</label>
                        <Input data-testid="mark-sold-qty" type="number" min={1} max={available} value={qty} onChange={(e) => setQty(e.target.value.replace(/\D/g, ""))} className="mt-1" />
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Buyer name (optional)</label>
                        <Input
                            data-testid="mark-sold-buyer"
                            value={buyer}
                            onChange={(e) => setBuyer(e.target.value)}
                            placeholder={channel === "external" ? "e.g. Walk-in customer" : "e.g. Rahul Sharma"}
                            className="mt-1"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                    <Button data-testid="mark-sold-confirm" disabled={busy} onClick={submit} className="aduku-yellow-btn hover:bg-aduku-yellow-600">
                        {busy ? "Recording..." : "Record sale"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
